import { z } from "zod";
import {
  BulletPointType,
  ContentGroupKind,
  defaultSettings,
  LanguageCode,
  MAX_CONTENT_GROUPS,
} from "../../pages/playground/types";
import { PHRASE_TYPE_SLOTS } from "../../utils/content-groups";
import { normalizeFieldName } from "../../utils/fieldNames";

// All valid PhraseType ids for content group / export rule references.
// Mirrors genaiapp/enums.py:PhraseType — keep in sync.
const VALID_GROUP_IDS = Object.keys(PHRASE_TYPE_SLOTS);

// Content group formatting schema
const contentGroupFormattingSchema = z.object({
  paragraphWrapTag: z.string().default(""),
  phraseWrapTag: z.string().default(""),
  bulletPrefix: z.string().default(""),
  phraseSeparator: z.string().default("space"),
  keyValueMode: z.boolean().default(false),
  keyWrapTag: z.string().default(""),
});

// Content group phrases schema (reuses same logic as phraseValidation)
const contentGroupPhrasesSchema = z
  .object({
    minPhrases: z.number().optional(),
    maxPhrases: z.number().optional(),
    minCharacters: z.number().optional(),
    maxCharacters: z.number().optional(),
    minWords: z.number().optional(),
    maxWords: z.number().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.minPhrases !== undefined && data.minPhrases < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 1,
        inclusive: true,
        type: "number",
        message: "Minimum phrases must be at least 1",
        path: ["minPhrases"],
      });
    }
    if (data.maxPhrases !== undefined && data.maxPhrases < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 1,
        inclusive: true,
        type: "number",
        message: "Maximum phrases must be at least 1",
        path: ["maxPhrases"],
      });
    }
    if (
      data.maxPhrases !== undefined &&
      data.minPhrases !== undefined &&
      data.maxPhrases < data.minPhrases
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Maximum phrases cannot be less than minimum",
        path: ["maxPhrases"],
      });
    }
    if (
      data.maxCharacters !== undefined &&
      data.minCharacters !== undefined &&
      data.maxCharacters < data.minCharacters
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Maximum characters cannot be less than minimum",
        path: ["maxCharacters"],
      });
    }
    if (
      data.maxWords !== undefined &&
      data.minWords !== undefined &&
      data.maxWords < data.minWords
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Maximum words cannot be less than minimum",
        path: ["maxWords"],
      });
    }
  });

// Single content group schema
const contentGroupSchema = z.object({
  // Must be a known PhraseType id — otherwise master_file_sync.py silently
  // drops the group via try/except ValueError on PhraseType(group_id).
  id: z.string().refine((v) => VALID_GROUP_IDS.includes(v), {
    message: "Unknown content group id",
  }),
  kind: z.enum(["title", "description", "bullet_points"] as [
    ContentGroupKind,
    ...ContentGroupKind[],
  ]),
  label: z
    .string()
    .min(1, "Group label required")
    .refine((v) => v.trim().length > 0, "Group label required")
    .refine(
      (v) => normalizeFieldName(v).length > 0,
      "Group label normalizes to empty — use letters or digits",
    ),
  enabled: z.boolean(),
  sortOrder: z.number(),
  phrases: contentGroupPhrasesSchema,
  formatting: contentGroupFormattingSchema,
});

// Content groups array schema with cross-group validation
const contentGroupsSchema = z
  .array(contentGroupSchema)
  .max(MAX_CONTENT_GROUPS, `Maximum ${MAX_CONTENT_GROUPS} groups allowed`)
  .superRefine((groups, ctx) => {
    // Must have exactly one title group. Attach the error to the actual title
    // group's index when present so the UI can highlight the right card.
    const titleIndex = groups.findIndex((g) => g.kind === "title");
    const titleCount = groups.filter((g) => g.kind === "title").length;
    if (titleCount !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Exactly one title group is required",
        path: [titleIndex >= 0 ? titleIndex : 0, "kind"],
      });
    }

    // Group ids must be unique — two groups with the same id break getGroupById
    const idCounts = new Map<string, number[]>();
    groups.forEach((g, i) => {
      const arr = idCounts.get(g.id) || [];
      arr.push(i);
      idCounts.set(g.id, arr);
    });
    for (const [, indexes] of idCounts) {
      if (indexes.length > 1) {
        for (const idx of indexes) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Duplicate group id — each slot can be used by one group only`,
            path: [idx, "id"],
          });
        }
      }
    }

    // At least one enabled description
    const enabledDescriptions = groups.filter(
      (g) => g.kind === "description" && g.enabled
    );
    if (enabledDescriptions.length === 0) {
      const firstDescIndex = groups.findIndex((g) => g.kind === "description");
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "At least one description must be enabled",
        path: [firstDescIndex >= 0 ? firstDescIndex : 0, "enabled"],
      });
    }

    // Unique normalized labels across all groups — collisions silently drop
    // columns in master_file_sync.py and batch-table.tsx export.
    const normalizedSeen = new Map<string, number[]>();
    groups.forEach((g, i) => {
      const norm = normalizeFieldName(g.label);
      if (!norm) return; // empty handled by per-group label refine
      const arr = normalizedSeen.get(norm) || [];
      arr.push(i);
      normalizedSeen.set(norm, arr);
    });
    for (const [norm, indexes] of normalizedSeen) {
      if (indexes.length > 1) {
        for (const idx of indexes) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Label collides with another group on export column "${norm}"`,
            path: [idx, "label"],
          });
        }
      }
    }

    // Per-group phrase limit consistency. The strict "must specify min/max
    // chars OR words" rule has been removed: V1-migrated rows have all-zero
    // limits (= no constraint), and forcing a value would block save flow on
    // every legacy batch's first edit. Limit ordering (max >= min) is still
    // enforced in contentGroupPhrasesSchema.
    groups.forEach((group, index) => {
      if (!group.enabled && group.kind !== "title") return;

      const p = group.phrases;

      // Key-value mode: phrases must come in pairs — both min and max must be
      // defined AND even, and min must be >= 2 to actually form a key/value pair.
      if (group.formatting.keyValueMode) {
        if (p.minPhrases === undefined || p.minPhrases < 2) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${group.label}: Key-value mode requires min phrases >= 2`,
            path: [index, "phrases", "minPhrases"],
          });
        } else if (p.minPhrases % 2 !== 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${group.label}: Key-value mode requires even number of min phrases`,
            path: [index, "phrases", "minPhrases"],
          });
        }
        if (p.maxPhrases === undefined || p.maxPhrases < 2) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${group.label}: Key-value mode requires max phrases >= 2`,
            path: [index, "phrases", "maxPhrases"],
          });
        } else if (p.maxPhrases % 2 !== 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${group.label}: Key-value mode requires even number of max phrases`,
            path: [index, "phrases", "maxPhrases"],
          });
        }
      }
    });
  })
  .optional();

// Brand instructions schema
const brandInstructionsSchema = z
  .object({
    brand_context: z.string().optional().default(""),
    brand_rules: z.string().optional().default(""),
    brand_values: z.string().optional().default(""),
    brand_tone_of_voice: z.string().optional().default(""),
  })
  .optional()
  .default({
    brand_context: "",
    brand_rules: "",
    brand_values: "",
    brand_tone_of_voice: "",
  });

// Settings schema matches the defaultSettings structure
// V1 flat keys are kept as passthrough for backward compatibility but not validated
const settingsSchema = z
  .object({
    title_instructions: z.string().optional().nullable(),
    fabric_instructions: z.string().optional().nullable(),
    brand_instructions: brandInstructionsSchema,
    color_instructions: z.string().optional().nullable(),
    country_of_origin_instructions: z.string().optional().nullable(),
    details_instructions: z.string().optional().nullable(),
    description_instructions: z.string().optional().nullable(),
    phrasebook_brands: z.array(z.string()).optional().default([]),
    bullet_points_bullet: z.string().optional().default(""),
    languages_to_translate: z.array(z.string()).optional().default([]),
    lara_languages: z.array(z.string()).optional().default([]),
    lara_glossaries: z.array(z.string()).optional().default([]),
    lara_memories: z.array(z.string()).optional().default([]),
    lara_instructions: z.array(z.string()).optional().default([]),
    lara_style: z
      .enum(["faithful", "fluid", "creative"])
      .optional()
      .default("faithful"),
    title_templates: z.array(z.string()).optional().default([]),
    content_groups: contentGroupsSchema,
    export_rules: z
      .array(
        z.object({
          id: z.string().min(1),
          label: z
            .string()
            .min(1, "Export rule name required")
            .refine((v) => v.trim().length > 0, "Export rule name required")
            .refine(
              (v) => normalizeFieldName(v).length > 0,
              "Rule name normalizes to empty — use letters or digits",
            ),
          sourceGroups: z
            .array(z.string())
            .min(1, "Select at least one source group")
            .refine(
              (ids) => ids.every((id) => VALID_GROUP_IDS.includes(id)),
              "Source groups must reference known content groups",
            ),
          separator: z.string().optional(),
        })
      )
      .optional()
      .default([]),
  })
  .passthrough() // Allow V1 flat keys to pass through without validation
  .superRefine((settings, ctx) => {
    const groups = settings.content_groups || [];
    const rules = settings.export_rules || [];
    const enabledIds = new Set(groups.filter((g) => g.enabled).map((g) => g.id));

    // Rule sourceGroups must reference EXISTING + ENABLED groups, otherwise
    // the export silently drops the rule's contribution.
    rules.forEach((rule, ri) => {
      rule.sourceGroups.forEach((gid) => {
        if (!enabledIds.has(gid)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `References missing or disabled group "${gid}"`,
            path: ["export_rules", ri, "sourceGroups"],
          });
        }
      });
    });

    // Cross-collision: a rule label cannot share its normalized export
    // column name with any group label or another rule.
    //
    // Groups consumed by a rule are NOT exported as their own column
    // (batch-table.tsx + master_file_sync.py skip them via mergedGroupIds),
    // so a rule renaming its own source(s) does not actually collide. Build
    // a per-rule consumed-set and exclude it from the group collision check.
    const groupNorm = new Map<string, string>();
    const groupIdToNorm = new Map<string, string>();
    groups.forEach((g) => {
      const n = normalizeFieldName(g.label);
      if (n) {
        groupNorm.set(n, g.label);
        groupIdToNorm.set(g.id, n);
      }
    });
    const ruleNorm = new Map<string, number[]>();
    rules.forEach((r, i) => {
      const n = normalizeFieldName(r.label);
      if (!n) return;
      // Names of groups this rule consumes — those columns won't exist at export.
      const consumedNames = new Set(
        r.sourceGroups
          .map((id) => groupIdToNorm.get(id))
          .filter((x): x is string => !!x),
      );
      if (groupNorm.has(n) && !consumedNames.has(n)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Rule export column "${n}" collides with group "${groupNorm.get(n)}"`,
          path: ["export_rules", i, "label"],
        });
      }
      const arr = ruleNorm.get(n) || [];
      arr.push(i);
      ruleNorm.set(n, arr);
    });
    for (const [n, indexes] of ruleNorm) {
      if (indexes.length > 1) {
        for (const idx of indexes) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Rule export column "${n}" is used by another rule`,
            path: ["export_rules", idx, "label"],
          });
        }
      }
    }
  })
  .optional()
  .default(() => defaultSettings);

// Archetype scores should be between 0 and 10
const archetypeScoreSchema = z.number().min(0).max(10).default(0);

// Narrative perspective valid values
const narrativePerspectiveEnum = z.enum([
  "SECOND_PERSON",
  "THIRD_PERSON",
  "FIRST_PERSON_PLURAL",
  "SECOND_PERSON_PLURAL",
]);

// Schema base comune con campi condivisi tra brand e batch
const baseSchema = z.object({
  original_language: z.nativeEnum(LanguageCode).default(LanguageCode.IT),
  archetype_score_angelo_custode: archetypeScoreSchema,
  archetype_score_amante: archetypeScoreSchema,
  archetype_score_burlone: archetypeScoreSchema,
  archetype_score_sovrano: archetypeScoreSchema,
  archetype_score_innocente: archetypeScoreSchema,
  archetype_score_ribelle: archetypeScoreSchema,
  archetype_score_uomo_comune: archetypeScoreSchema,
  archetype_score_creatore: archetypeScoreSchema,
  archetype_score_mago: archetypeScoreSchema,
  archetype_score_saggio: archetypeScoreSchema,
  archetype_score_eroe: archetypeScoreSchema,
  archetype_score_esploratore: archetypeScoreSchema,

  expressions: z.array(z.string()).optional().default([]),
  narrative_perspective: narrativePerspectiveEnum.default("THIRD_PERSON"),
  words_to_avoid: z.array(z.string()).optional().default([]),

  // Order of bullet points in UI
  bullet_points_order: z
    .array(z.enum(Object.values(BulletPointType) as [string, ...string[]]))
    .default([]),

  // Settings object containing phrase configuration
  settings: settingsSchema,
});

const brandSchema = baseSchema.extend({
  name: z.string().min(1, "Name required"),
  logo_name: z.string().min(1, "Logo name required"),
  gdrive_folder_id: z.string().optional().default(""),

  // Optional fields for brands
  brand_id: z.string().optional().default(""),
  collection_name: z.string().optional().default(""),
  season: z.string().optional().default(""),
});

const batchSchema = baseSchema.extend({
  name: z.string().min(1, "Name required"),
  gdrive_folder_id: z.string().optional().default(""),
  brand_id: z.string().min(1, "Brand ID required for batches"),
  collection_name: z.string().optional().default(""),
  season: z.string().optional().default(""),
});

export const brandBatchSchema = z.discriminatedUnion("$type", [
  brandSchema.extend({ $type: z.literal("brand") }),
  batchSchema.extend({ $type: z.literal("batch") }),
]);
